import React, { useMemo } from "react";
import { format, isValid, parseISO } from "date-fns";
import { Paperclip } from "lucide-react";
import { GlobalTableActionButton } from "../../../../../global/GlobalTableActionButton";
import GlobalDirectoryFiltersSection from "../../../../../global/directory/GlobalDirectoryFiltersSection";
import GlobalDirectoryGridSection from "../../../../../global/directory/GlobalDirectoryGridSection";
import {
  GlobalDirectoryShell,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
  GlobalDirectoryPaginationSection,
} from "../../../../../global/directory";

export const MAINTENANCE_LIST_COLUMN_VISIBILITY_KEY =
  "girinestham.maintenanceDirectory.columnVisibility";

export const MAINTENANCE_DIRECTORY_COLUMN_DEFS = [
  { id: "created_at", label: "Date", defaultVisible: true },
  { id: "type", label: "Type", defaultVisible: true },
  { id: "asset_area", label: "Asset / area", defaultVisible: true },
  { id: "issue_description", label: "Issue", defaultVisible: false },
  { id: "reported_by", label: "Reported by", defaultVisible: true },
  { id: "assigned_to_name", label: "Assigned to", defaultVisible: true },
  { id: "status", label: "Status", defaultVisible: true },
  { id: "cost_incurred", label: "Cost", defaultVisible: true },
  { id: "attachment", label: "Attachment", defaultVisible: true },
];

const DIRECTORY_FILTER_COLUMNS = [
  { id: "id", label: "Request ID" },
  ...MAINTENANCE_DIRECTORY_COLUMN_DEFS.filter((c) => c.id !== "attachment"),
];

function loadMaintenanceColumnVisibility(
  storageKey = MAINTENANCE_LIST_COLUMN_VISIBILITY_KEY,
) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
}

export function createInitialMaintenanceColumnVisibility(
  storageKey = MAINTENANCE_LIST_COLUMN_VISIBILITY_KEY,
) {
  const saved = loadMaintenanceColumnVisibility(storageKey);
  const initial = {};
  for (const c of MAINTENANCE_DIRECTORY_COLUMN_DEFS) {
    initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
  }
  return initial;
}

function formatDateCell(value) {
  if (value == null || value === "") return "—";
  try {
    const raw = String(value).trim();
    let d = parseISO(raw);
    if (!isValid(d) && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
      d = new Date(raw.replace(" ", "T"));
    }
    if (!isValid(d)) d = new Date(raw);
    return isValid(d) ? format(d, "PP") : "—";
  } catch {
    return "—";
  }
}

function requestCreatedIso(row) {
  const raw = row?.created_at;
  if (raw == null || raw === "") return "";
  try {
    const s = String(raw).trim();
    let d = parseISO(s);
    if (!isValid(d)) d = new Date(s);
    if (!isValid(d)) return "";
    return format(d, "yyyy-MM-dd");
  } catch {
    return "";
  }
}

function maintenanceFilterValueDisplay(col, row, masters) {
  switch (col) {
    case "id":
      return String(row.id ?? "").trim();
    case "created_at": {
      const iso = requestCreatedIso(row);
      return iso ? iso : formatDateCell(row.created_at);
    }
    case "type": {
      const t = row.type;
      if (t != null && String(t).trim() !== "") return String(t).trim();
      if (!masters?.maintenanceTypes || !row.maintenance_type_id) return "";
      const mt = masters.maintenanceTypes.find(
        (x) => String(x.id) === String(row.maintenance_type_id),
      );
      return mt?.type_name ? String(mt.type_name).trim() : "";
    }
    case "status": {
      const s = row.status;
      if (s != null && String(s).trim() !== "") return String(s).trim();
      if (!masters?.maintenanceStatuses || row.status_id == null) return "";
      const st = masters.maintenanceStatuses.find(
        (x) => String(x.id) === String(row.status_id),
      );
      return st?.status_name ? String(st.status_name).trim() : "";
    }
    case "assigned_to_name": {
      const n = row.assigned_to_name;
      if (n != null && String(n).trim() !== "") return String(n).trim();
      if (!masters?.users || !row.assigned_to) return "";
      const u = masters.users.find((x) => String(x.id) === String(row.assigned_to));
      return u?.name ? String(u.name).trim() : "";
    }
    case "reported_by": {
      const n = row.reported_by;
      if (n != null && String(n).trim() !== "") return String(n).trim();
      if (!masters?.users || row.reported_by_user_id == null) return "";
      const u = masters.users.find((x) => String(x.id) === String(row.reported_by_user_id));
      return u?.name ? String(u.name).trim() : "";
    }
    case "cost_incurred":
      if (row.cost_incurred == null || row.cost_incurred === "") return "";
      return String(Number(row.cost_incurred));
    case "attachment":
      return row.attachment && String(row.attachment).trim() !== "" ? "Attached" : "";
    default:
      return String(row[col] ?? "").trim();
  }
}

function maintenanceAttachmentHref(row) {
  const raw = row?.attachment;
  if (raw == null || String(raw).trim() === "") return null;
  return String(raw).trim();
}

export function maintenanceDirectoryCellValue(columnId, row, masters) {
  switch (columnId) {
    case "id":
      return row.id != null ? `#${row.id}` : "—";
    case "created_at":
      return formatDateCell(row.created_at);
    case "type":
      return maintenanceFilterValueDisplay("type", row, masters) || "—";
    case "issue_description": {
      const t = row.issue_description;
      if (t == null || String(t).trim() === "") return "—";
      const s = String(t);
      return s.length > 120 ? `${s.slice(0, 117)}…` : s;
    }
    case "cost_incurred":
      if (row.cost_incurred == null || row.cost_incurred === "") return "—";
      return `₹${Number(row.cost_incurred).toFixed(2)}`;
    case "assigned_to_name":
      return maintenanceFilterValueDisplay("assigned_to_name", row, masters) || "—";
    case "reported_by":
      return maintenanceFilterValueDisplay("reported_by", row, masters) || "—";
    case "status":
      return maintenanceFilterValueDisplay("status", row, masters) || "—";
    case "attachment":
      if (!row.attachment || String(row.attachment).trim() === "") return "—";
      return "Attached";
    default:
      return row[columnId] ?? "—";
  }
}

export function maintenanceDefaultDirectoryFilters() {
  return {
    user_search: "",
    column: "",
    value: "",
  };
}

function uniqSelectOptions(...groups) {
  const seen = new Set();
  const out = [];
  for (const opts of groups) {
    for (const o of opts) {
      if (!o) continue;
      const v = String(o.value ?? "").trim();
      if (!v || seen.has(v)) continue;
      seen.add(v);
      const label = String(o.label ?? v).trim() || v;
      out.push({ value: v, label });
    }
  }
  return out.sort((a, b) =>
    a.label.localeCompare(b.label, undefined, {
      numeric: true,
      sensitivity: "base",
    }),
  );
}

function MaintenanceDirectoryFilters({
  draftFilters,
  onDraftChange,
  onClear,
  requests,
  masters,
  secondaryStart,
  secondaryEnd,
}) {
  const valueFilterOptions = useMemo(() => {
    const col = draftFilters.column;
    if (!col) return [];

    if (col === "type") {
      const fromLookup = (masters?.maintenanceTypes || []).map((t) => {
        const n = String(t.type_name ?? "").trim();
        return n ? { value: n, label: n } : null;
      });
      const fromRows = [];
      for (const row of requests) {
        const n = row.type;
        if (n == null || String(n).trim() === "") continue;
        const v = String(n).trim();
        fromRows.push({ value: v, label: v });
      }
      return uniqSelectOptions(fromLookup.filter(Boolean), fromRows);
    }

    if (col === "status") {
      const fromLookup = (masters?.maintenanceStatuses || []).map((s) => {
        const n = String(s.status_name ?? "").trim();
        return n ? { value: n, label: n } : null;
      });
      const fromRows = [];
      for (const row of requests) {
        const n = row.status;
        if (n == null || String(n).trim() === "") continue;
        const v = String(n).trim();
        fromRows.push({ value: v, label: v });
      }
      const staticCancelled = { value: "Cancelled", label: "Cancelled" };
      return uniqSelectOptions(fromLookup.filter(Boolean), fromRows, [staticCancelled]);
    }

    if (col === "reported_by") {
      const fromLookup = (masters?.users || []).map((u) => {
        const n = String(u.name ?? "").trim();
        if (!n) return null;
        const code = u.user_code ? ` (${String(u.user_code).trim()})` : "";
        return { value: String(u.id), label: `${n}${code}` };
      });
      const fromRows = [];
      for (const row of requests) {
        const id = row.reported_by_user_id;
        const label = maintenanceFilterValueDisplay("reported_by", row, masters);
        if (id == null || !label) continue;
        fromRows.push({ value: String(id), label });
      }
      return uniqSelectOptions(fromLookup.filter(Boolean), fromRows);
    }

    if (col === "created_at") {
      const seen = new Set();
      const opts = [];
      for (const row of requests) {
        const iso = requestCreatedIso(row);
        if (!iso || seen.has(iso)) continue;
        seen.add(iso);
        opts.push({
          value: iso,
          label: formatDateCell(row.created_at),
        });
      }
      return opts.sort((a, b) => a.value.localeCompare(b.value));
    }

    const seen = new Set();
    const opts = [];
    for (const row of requests) {
      const label = maintenanceFilterValueDisplay(col, row, masters);
      if (!label) continue;
      if (!seen.has(label)) {
        seen.add(label);
        opts.push({ value: label, label });
      }
    }
    return opts.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
  }, [draftFilters.column, requests, masters]);

  return (
    <GlobalDirectoryFiltersSection
      className="ub-filters ub-ms-directory-filters"
      searchLabel="Search by username"
      searchPlaceholder="Type a name"
      searchValue={draftFilters.user_search}
      onSearchChange={(v) => onDraftChange({ ...draftFilters, user_search: v })}
      columnLabel="Column"
      columnValue={draftFilters.column}
      onColumnChange={(v) => onDraftChange({ ...draftFilters, column: v, value: "" })}
      gridColumnDefs={DIRECTORY_FILTER_COLUMNS}
      columnPlaceholderOption="Select"
      valueLabel="Value"
      valueValue={draftFilters.value}
      onValueChange={(v) => onDraftChange({ ...draftFilters, value: v })}
      valueOptions={valueFilterOptions}
      valuePlaceholderOption="All"
      onClear={onClear}
      clearFilterLabel="Clear filter"
      secondaryStart={secondaryStart}
      secondaryEnd={secondaryEnd}
    />
  );
}

function getMaintenanceStatusBadgeClass(statusId, statusLabel) {
  const id = Number(statusId);
  const n = String(statusLabel || "")
    .trim()
    .toLowerCase();
  if (id === 3 || n.includes("complete")) return "status-badge status-badge--success";
  if (id === 4 || n.includes("cancel")) return "status-badge status-badge--danger";
  if (id === 2 || n.includes("progress")) return "status-badge status-badge--info";
  if (id === 1 || n.includes("pending")) return "status-badge status-badge--warning";
  return "status-badge";
}

function MaintenanceDirectoryAttachmentCell({ request }) {
  const href = maintenanceAttachmentHref(request);
  if (!href) return "—";
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title="View attachment"
      className="maintenance-directory-attachment-link"
      aria-label="View attachment"
    >
      <Paperclip size={16} aria-hidden />
    </a>
  );
}

function MaintenanceRequestsTable({
  requests,
  masters,
  columnVisibility,
  loading,
  onView,
  onEdit,
  onAssign,
  onStatus,
  onDelete,
  directoryActionMode = "admin",
  rowSelection = null,
}) {
  const showAdminActions = directoryActionMode === "admin";
  const visibleDataColumns = useMemo(
    () => MAINTENANCE_DIRECTORY_COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility],
  );

  const pageIds = useMemo(
    () =>
      requests
        .map((r) => Number(r.id))
        .filter((n) => Number.isFinite(n)),
    [requests],
  );
  const allPageSelected =
    rowSelection?.enabled &&
    pageIds.length > 0 &&
    pageIds.every((id) => rowSelection.selectedIds.has(id));
  const somePageSelected =
    rowSelection?.enabled && pageIds.some((id) => rowSelection.selectedIds.has(id));

  const tableHeaders = useMemo(
    () => [
      ...(rowSelection?.enabled
        ? [
            {
              key: "select",
              label: (
                <input
                  type="checkbox"
                  aria-label="Select all on this page"
                  checked={allPageSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = somePageSelected && !allPageSelected;
                  }}
                  onChange={(e) => rowSelection.onToggleAll?.(e.target.checked)}
                />
              ),
              className: "ub-table__col-select",
            },
          ]
        : []),
      ...visibleDataColumns.map((c) => ({ key: c.id, label: c.label })),
      {
        key: "actions",
        label: "Actions",
        className: "ub-table__col-actions",
      },
    ],
    [
      visibleDataColumns,
      rowSelection?.enabled,
      allPageSelected,
      somePageSelected,
      rowSelection?.onToggleAll,
    ],
  );

  const showRows = !loading && requests.length > 0;

  return (
    <GlobalDirectoryGridSection
      loading={loading}
      loadingMessage="Loading maintenance requests…"
      headers={tableHeaders}
      isEmpty={!loading && requests.length === 0}
      emptyMessage="No maintenance requests found."
      tableAriaLabel="Maintenance requests directory"
      wrapClassName="ub-table-wrap"
      tableClassName="ub-table"
    >
      {showRows
        ? requests.map((request) => {
            const statusLabel =
              request.status ||
              masters?.maintenanceStatuses?.find(
                (s) => String(s.id) === String(request.status_id),
              )?.status_name ||
              "—";
            const rowId = Number(request.id);
            const rowSelectable = Number.isFinite(rowId);
            return (
              <tr key={request.id}>
                {rowSelection?.enabled ? (
                  <td className="ub-table__col-select checkbox-cell">
                    <input
                      type="checkbox"
                      aria-label={`Select request ${request.id}`}
                      checked={rowSelectable && rowSelection.selectedIds.has(rowId)}
                      onChange={() => rowSelectable && rowSelection.onToggle?.(rowId)}
                    />
                  </td>
                ) : null}
                {visibleDataColumns.map((col) => (
                  <td key={col.id}>
                    {col.id === "status" ? (
                      <span
                        className={getMaintenanceStatusBadgeClass(
                          request.status_id,
                          statusLabel,
                        )}
                      >
                        {statusLabel}
                      </span>
                    ) : col.id === "attachment" ? (
                      <MaintenanceDirectoryAttachmentCell request={request} />
                    ) : (
                      maintenanceDirectoryCellValue(col.id, request, masters)
                    )}
                  </td>
                ))}
                <td className="ub-table__col-actions">
                  <div className="ub-table-actions">
                    {onView ? (
                      <GlobalTableActionButton
                        action="view"
                        onClick={() => onView(request)}
                        title="View details"
                      />
                    ) : null}
                    {onEdit && showAdminActions ? (
                      <GlobalTableActionButton
                        action="edit"
                        onClick={() => onEdit(request)}
                        title="Edit request"
                      />
                    ) : null}
                    {showAdminActions && onAssign ? (
                      <GlobalTableActionButton
                        action="assign"
                        onClick={() => onAssign(request)}
                        title="Assign staff"
                      />
                    ) : null}
                    {showAdminActions && onStatus ? (
                      <GlobalTableActionButton
                        action="status"
                        onClick={() => onStatus(request)}
                        title="Update status"
                      />
                    ) : null}
                    {showAdminActions && onDelete ? (
                      <GlobalTableActionButton
                        action="delete"
                        onClick={() => onDelete(request)}
                        title="Delete request"
                      />
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })
        : null}
    </GlobalDirectoryGridSection>
  );
}

export default function MaintenanceDirectorySection({
  requests,
  masters,
  draftDirectoryFilters,
  onDraftDirectoryFiltersChange,
  onClearDirectoryFilters,
  columnVisibility,
  onColumnVisibilityChange,
  getMaintenanceExportData,
  loadingRequests,
  directoryPage,
  directoryTotalPages,
  directoryTotal,
  directoryPageSize,
  onDirectoryPageChange,
  onView,
  onEdit,
  onAssign,
  onStatus,
  onDelete,
  sectionDomId = "md-list",
  columnVisibilityStorageKey = MAINTENANCE_LIST_COLUMN_VISIBILITY_KEY,
  directoryActionMode = "admin",
  directoryPaginationLabel = "Maintenance directory pagination",
  directoryPanelHint = "Search and manage maintenance requests across the campus.",
  embedded = false,
  approvals = null,
  rowSelection = null,
}) {
  const shell = (
    <GlobalDirectoryShell
      filters={
        <MaintenanceDirectoryFilters
          draftFilters={draftDirectoryFilters}
          onDraftChange={onDraftDirectoryFiltersChange}
          onClear={onClearDirectoryFilters}
          requests={requests}
          masters={masters}
          secondaryStart={
            <GlobalDirectoryColumnPicker
              columnDefs={MAINTENANCE_DIRECTORY_COLUMN_DEFS}
              visibility={columnVisibility}
              onVisibilityChange={onColumnVisibilityChange}
              storageKey={columnVisibilityStorageKey}
            />
          }
          secondaryEnd={
            getMaintenanceExportData ? (
              <GlobalDirectoryExportMenu
                getExportData={getMaintenanceExportData}
                filenameBase="maintenance_directory"
              />
            ) : null
          }
        />
      }
      approvals={approvals}
      grid={
        <MaintenanceRequestsTable
          requests={requests}
          masters={masters}
          columnVisibility={columnVisibility}
          loading={loadingRequests}
          onView={onView}
          onEdit={onEdit}
          onAssign={onAssign}
          onStatus={onStatus}
          onDelete={onDelete}
          directoryActionMode={directoryActionMode}
          rowSelection={rowSelection}
        />
      }
      pagination={
        <GlobalDirectoryPaginationSection
          page={directoryPage}
          totalPages={directoryTotalPages}
          total={directoryTotal}
          pageSize={directoryPageSize}
          onPageChange={onDirectoryPageChange}
          label={directoryPaginationLabel}
        />
      }
    />
  );

  if (embedded) {
    return (
      <div id={sectionDomId} className="mr-embedded-directory">
        {shell}
      </div>
    );
  }

  return (
    <div
      id={sectionDomId}
      className="user-builder-panel"
      style={{ scrollMarginTop: "2rem" }}
    >
      <div className="user-builder-panel__head">
        <h3 className="user-builder-panel__title">Directory</h3>
        <p className="user-builder-panel__hint">{directoryPanelHint}</p>
      </div>
      {shell}
    </div>
  );
}
